/**
 * KetoFusionOnboarding — 3-step enrollment wizard.
 *
 * Step 0: WHY (the science / promise)
 * Step 1: COMMITMENT (3 non-negotiables checklist)
 * Step 2: START (confirm + POST /start)
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Flame, ChevronLeft, ChevronRight, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const COMMITMENTS = [
  { id: "sleep", label: "Vou priorizar 7-9h de sono — é a maior alavanca." },
  { id: "training", label: "Vou treinar força composta 3x por semana." },
  { id: "supplements", label: "Vou aderir ao stack: Zn / Vit D / Mg / Ω3 quando indicado." },
  { id: "tracking", label: "Vou medir-me ao Dia 1, 21, 42, 63 e 84." },
];

export default function KetoFusionOnboarding({ open, onClose, onEnrolled }) {
  const [step, setStep] = useState(0);
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);

  const allChecked = COMMITMENTS.every((c) => checked[c.id]);

  const enroll = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/keto-fusion/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || "Erro ao iniciar Keto Fusion");
        return;
      }
      toast.success(data.already_enrolled ? "Já estavas inscrito ✨" : "Keto Fusion activado 🔥");
      onEnrolled?.(data.enrollment);
    } catch (e) {
      toast.error("Erro de rede");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-orange-500/30 max-w-2xl p-0 overflow-hidden" data-testid="fusion-wizard">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-6">
            {[0, 1, 2].map((s) => (
              <div key={s} className="h-1 flex-1 rounded-full transition-colors" style={{ background: s <= step ? "#f97316" : "#27272a" }} />
            ))}
          </div>

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="why" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h2 className="text-2xl font-bold text-zinc-100 mb-1">
                  <Flame className="inline w-5 h-5 text-orange-400 mr-2" />
                  Porquê Keto Fusion?
                </h2>
                <p className="text-sm text-zinc-400 mb-5">12 semanas. 84 missões. Recomposição corporal + performance híbrida.</p>

                <div className="space-y-3">
                  {[
                    { title: "Bloco 1 — Foundation (W1-3)", desc: "Adaptação cetogénica + fasting. Switch metabólico instalado.", color: "#84cc16" },
                    { title: "Bloco 2 — Carb Cycling (W4-6)", desc: "Primeiros refeeds estratégicos pós-treino. Força a aumentar.", color: "#f97316" },
                    { title: "Bloco 3 — Performance (W7-9)", desc: "1RM tests + volume + recomposição visível.", color: "#a855f7" },
                    { title: "Bloco 4 — Solidification (W10-12)", desc: "Maintenance + transição para vida fora do programa.", color: "#22c55e" },
                  ].map((b, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * i }}
                      className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/60 border border-zinc-800"
                    >
                      <div className="w-1 h-12 rounded-full" style={{ background: b.color }} />
                      <div>
                        <p className="text-sm font-semibold text-zinc-100">{b.title}</p>
                        <p className="text-xs text-zinc-400 mt-0.5">{b.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="flex justify-end mt-6">
                  <Button onClick={() => setStep(1)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="fusion-wizard-next-commit">
                    O meu compromisso <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="commit" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h2 className="text-2xl font-bold text-zinc-100 mb-1">O teu compromisso</h2>
                <p className="text-sm text-zinc-400 mb-5">4 pilares inegociáveis. Marca todos para activar o programa.</p>

                <div className="space-y-3">
                  {COMMITMENTS.map((c) => (
                    <label
                      key={c.id}
                      htmlFor={`commit-${c.id}`}
                      className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/60 border border-zinc-800 hover:border-orange-500/40 transition-colors cursor-pointer"
                    >
                      <Checkbox
                        id={`commit-${c.id}`}
                        checked={!!checked[c.id]}
                        onCheckedChange={(v) => setChecked((s) => ({ ...s, [c.id]: !!v }))}
                        className="mt-0.5 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                        data-testid={`fusion-commit-${c.id}`}
                      />
                      <span className="text-sm text-zinc-200 leading-relaxed">{c.label}</span>
                    </label>
                  ))}
                </div>

                <div className="flex justify-between mt-6">
                  <Button variant="ghost" onClick={() => setStep(0)}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
                  </Button>
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!allChecked}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="fusion-wizard-next-start"
                  >
                    Pronto para começar <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="start" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <div className="text-center py-4">
                  <motion.div
                    className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-orange-500/20 border-2 border-orange-500 mb-5"
                    animate={{ scale: [1, 1.06, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Sparkles className="w-12 h-12 text-orange-400" />
                  </motion.div>
                  <h2 className="text-2xl font-bold text-zinc-100 mb-2">Hoje é o teu Dia 1.</h2>
                  <p className="text-sm text-zinc-300 max-w-md mx-auto leading-relaxed mb-6">
                    Vais receber missões diárias adaptadas ao teu organismo. Spirit acompanha. Tu executas. 84 dias daqui a uma nova versão.
                  </p>
                  <Button onClick={enroll} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white text-base px-8 h-12" data-testid="fusion-wizard-enroll">
                    {saving ? "A activar..." : (
                      <>
                        <Flame className="w-5 h-5 mr-2" /> Activar Keto Fusion 🔥
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
